<!--
  @component ImportDropZone
  @description File drop zone for importing bibliography files
-->
<script lang="ts">
  import { createEventDispatcher } from 'svelte';
  import { t } from '@/i18n';
  import type { ImportFormat } from '@services/fileImportService';

  export let selectedFile: File | null = null;
  export let detectedFormat: ImportFormat | null = null;

  const dispatch = createEventDispatcher<{
    fileSelect: { file: File };
    clear: void;
  }>();

  let isDragging = false;
  let fileInput: HTMLInputElement;

  function handleDragOver(event: DragEvent) {
    event.preventDefault();
    isDragging = true;
  }

  function handleDragLeave(event: DragEvent) {
    event.preventDefault();
    isDragging = false;
  }

  function handleDrop(event: DragEvent) {
    event.preventDefault();
    isDragging = false;

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      dispatch('fileSelect', { file: files[0] });
    }
  }

  function handleFileSelect(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      dispatch('fileSelect', { file: input.files[0] });
    }
  }

  function triggerFileSelect() {
    fileInput?.click();
  }

  function handleClear() {
    if (fileInput) fileInput.value = '';
    dispatch('clear');
  }

  function getFormatName(format: ImportFormat | null): string {
    if (!format) return '';
    const names: Record<ImportFormat, string> = {
      bibtex: 'BibTeX',
      ris: 'RIS',
      csljson: 'CSL JSON',
      mods: 'MODS',
      refer: 'Refer/BibIX',
      tei: 'TEI',
    };
    return names[format] || format.toUpperCase();
  }
</script>

<!-- Hidden file input -->
<input
  type="file"
  bind:this={fileInput}
  on:change={handleFileSelect}
  accept=".bib,.bibtex,.ris,.json,.xml,.mods,.tei,.txt"
  class="hidden-input"
/>

<!-- Drop Zone -->
<div
  class="drop-zone"
  class:dragging={isDragging}
  class:has-file={selectedFile}
  on:dragover={handleDragOver}
  on:dragleave={handleDragLeave}
  on:drop={handleDrop}
  on:click={triggerFileSelect}
  role="button"
  tabindex="0"
  on:keydown={(e) => e.key === 'Enter' && triggerFileSelect()}
>
  {#if selectedFile}
    <div class="file-info">
      <span class="file-icon">📄</span>
      <div class="file-details">
        <span class="file-name">{selectedFile.name}</span>
        {#if detectedFormat}
          <span class="file-format"
            >{$t('import.detectedFormat')}: {getFormatName(detectedFormat)}</span
          >
        {/if}
      </div>
      <button
        class="clear-file-btn"
        on:click|stopPropagation={handleClear}
        aria-label={$t('common.clear')}
      >
        &times;
      </button>
    </div>
  {:else}
    <div class="drop-zone-content">
      <span class="drop-icon">📥</span>
      <p class="drop-text">{$t('import.dropZoneText')}</p>
      <p class="drop-hint">{$t('import.dropZoneHint')}</p>
      <button class="btn btn-secondary browse-btn" on:click|stopPropagation={triggerFileSelect}>
        {$t('import.browseFiles')}
      </button>
    </div>
  {/if}
</div>

<!-- Supported formats info -->
<div class="formats-info">
  <span class="formats-label">{$t('import.supportedFormats')}:</span>
  <span class="formats-list">BibTeX, RIS, CSL JSON</span>
</div>

<style>
  .hidden-input {
    display: none;
  }

  .drop-zone {
    border: 2px dashed var(--color-border, #555);
    border-radius: 8px;
    padding: 40px 20px;
    text-align: center;
    cursor: pointer;
    transition: all 0.2s;
    margin-bottom: 16px;
  }

  .drop-zone:hover {
    border-color: var(--color-accent, #5c7cfa);
    background: rgba(92, 124, 250, 0.05);
  }

  .drop-zone.dragging {
    border-color: var(--color-accent, #5c7cfa);
    background: rgba(92, 124, 250, 0.1);
    border-style: solid;
  }

  .drop-zone.has-file {
    border-style: solid;
    border-color: var(--color-success, #40c057);
    background: rgba(64, 192, 87, 0.05);
    padding: 20px;
  }

  .drop-zone-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
  }

  .drop-icon {
    font-size: 48px;
    opacity: 0.7;
  }

  .drop-text {
    font-size: 16px;
    color: var(--color-text-primary, #e8e8e8);
    margin: 0;
  }

  .drop-hint {
    font-size: 13px;
    color: var(--color-text-muted, #888);
    margin: 0;
  }

  .browse-btn {
    margin-top: 12px;
  }

  .file-info {
    display: flex;
    align-items: center;
    gap: 12px;
    text-align: left;
  }

  .file-icon {
    font-size: 32px;
  }

  .file-details {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .file-name {
    font-size: 14px;
    font-weight: 500;
    color: var(--color-text-primary, #e8e8e8);
  }

  .file-format {
    font-size: 12px;
    color: var(--color-text-secondary, #aaa);
  }

  .clear-file-btn {
    background: none;
    border: none;
    font-size: 20px;
    color: var(--color-text-secondary, #aaa);
    cursor: pointer;
    padding: 4px 8px;
  }

  .clear-file-btn:hover {
    color: var(--color-text-primary, #e8e8e8);
  }

  .formats-info {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
    color: var(--color-text-muted, #888);
    margin-bottom: 16px;
  }

  .formats-label {
    font-weight: 500;
  }

  .btn {
    padding: 10px 16px;
    border-radius: 4px;
    font-size: 14px;
    cursor: pointer;
    transition: background-color 0.2s;
  }

  .btn-secondary {
    background: var(--color-bg-tertiary, #4a4a4a);
    border: 1px solid var(--color-border, #555);
    color: var(--color-text-primary, #e8e8e8);
  }

  .btn-secondary:hover:not(:disabled) {
    background: var(--color-bg-hover, #555);
  }
</style>
